const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// The report service parses "23 Mar 2026" out of these strings to compute the
// review date. Compact forms (23-03-2026) do not parse and the date prints blank.
export const formatReportDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return `${String(date.getDate()).padStart(2, "0")} ${
    MONTHS[date.getMonth()]
  } ${date.getFullYear()}`;
};

// The case API returns the patient as firstname/lastname (lowercase), while the
// header prefers the linked external report for age/gender/dob. Read the same
// sources the UI displays, so the PDF cannot disagree with the screen.
const patientName = (patient = {}) =>
  `${patient.firstname || patient.firstName || ""} ${
    patient.lastname || patient.lastName || ""
  }`.trim();

const ageFromDob = (dob) => {
  if (!dob) return "";
  const born = new Date(dob);
  if (isNaN(born.getTime())) return "";
  const now = new Date();
  let age = now.getFullYear() - born.getFullYear();
  const monthDiff = now.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : "";
};

const daysBetween = (from, to) => {
  if (!from || !to) return "";
  const start = new Date(from);
  const end = new Date(to);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";
  return Math.max(0, Math.round((end - start) / 86400000));
};

const patientAddress = (patient = {}) =>
  [
    patient.addressLine1,
    patient.addressLine2,
    patient.city,
    patient.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

const patientPhone = (patient = {}) => {
  const digits = String(patient.mobile || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
};

/**
 * Build the Ayurvedic prescription report payload.
 *
 * Every key we own is sent even when blank.
 *
 * The four auto-filled sections are the exception, because the report service
 * reads them by key presence: an omitted key means "source it for us", while a
 * present-but-empty key means "the doctor deleted this section" and drops it
 * from the PDF. So each is sent only when the doctor left content in it — an
 * untouched, still-empty section is omitted so the service fills it.
 */
export const buildPrescriptionReportPayload = ({
  patient = {},
  doctor = {},
  hospital = {},
  complaints = [],
  medicines = [],
  nextFollowup,
  repeatTests,
  consultDate,
  rootCauseFactors = [],
  lifestylePlan = [],
  yogaAsanas = [],
  nutrition = {},
  externalReport = {},
} = {}) => {
  const consultedOn = consultDate || new Date();
  const external = externalReport.attributes || externalReport;
  const dob = external.time_of_birth || patient.dob;

  const sourcedSections = {};
  const cleanRootCause = rootCauseFactors.filter((row) => row && row.term);
  const cleanLifestyle = lifestylePlan.filter((row) => row && row.trim());
  const cleanYoga = yogaAsanas.filter((row) => row && row.asana);
  const eat = (nutrition.add || []).filter(Boolean);
  const avoid = (nutrition.avoid || []).filter(Boolean);

  if (cleanRootCause.length) sourcedSections.rootCauseFactors = cleanRootCause;
  if (cleanLifestyle.length) sourcedSections.lifestylePlan = cleanLifestyle;
  if (cleanYoga.length) sourcedSections.yogaAsanas = cleanYoga;
  if (eat.length || avoid.length) {
    sourcedSections.nutrition = { add: eat, avoid: avoid };
  }

  return {
    ...sourcedSections,
    booking_id: patientPhone(patient),
    doctor: {
      name: `${doctor.firstName || ""} ${doctor.lastName || ""}`.trim(),
      specialty: doctor.specialty || "",
      qualification: doctor.qualification || "",
      registrationNo: doctor.registrationNo || "",
    },
    hospital: {
      name: hospital.name || "",
      address: hospital.address || "",
      phone: hospital.phone || "",
      email: hospital.email || "",
    },
    patient: {
      name: patientName(patient),
      age: external.age || ageFromDob(dob),
      gender: external.gender || patient.gender || "",
      phone: patientPhone(patient),
      uhid: patient.patientId || "",
      consultDateTime: formatReportDate(consultedOn),
      address: patientAddress(patient),
    },
    presentingComplaints: complaints
      .filter((item) => item && item.complaint)
      .map((item) => ({
        complaint: item.complaint || "",
        duration: item.duration || "",
      })),
    medicines: medicines
      .filter((item) => item && item.name)
      .map((item) => ({
        medicine: item.name || "",
        dose: item.dosage || "",
        timing: item.medicineTime || "",
        frequency: item.frequency || "",
        duration: item.duration || "",
      })),
    followUp: {
      reviewDays: daysBetween(consultedOn, nextFollowup),
      reviewDate: formatReportDate(nextFollowup),
      repeatTests: repeatTests || "",
    },
  };
};
